import React, { lazy, Suspense } from 'react';
import TextOverflowIndicator from './TextOverflowIndicator';
import { richTextToHTML } from '../../utils/richTextModel';

// L'éditeur inline embarque TipTap/ProseMirror (~860 kB de source) : chargé
// en lazy au premier double-clic sur un calque texte, pas au boot de l'app.
// Le rendu LECTURE passe par richTextToHTML (pur, sans TipTap).
const InlineRichTextEditor = lazy(() => import('../InlineRichTextEditor'));
import { PT_TO_MM, getCssGradient } from './helpers';
import { REGULATORY_CONTENT_EDIT_ALLOWED } from '../../utils/regulatoryFlags';
import { useLabelStore } from '../../store/useLabelStore';

// TEXT_SCALE : technique scale-up × 10 pour éviter les artefacts sub-pixel
// du rendu du texte. Le contenu est rendu à 10× la taille puis scaled-down
// par CSS — la fontSize CSS réelle est de plusieurs dizaines de px (jamais
// 4-5px qui casseraient les espaces et le sub-pixel rendering).
const TEXT_SCALE = 10;

/**
 * Remet une chaîne `text-shadow` à l'échelle du wrapper grossi ×TEXT_SCALE.
 *
 * Toute longueur déclarée DANS ce wrapper est divisée par TEXT_SCALE à
 * l'affichage — c'est pourquoi fontSize, interlettrage et épaisseur de contour
 * sont déjà multipliés par lui plus bas. AVANT, la chaîne text-shadow était
 * posée telle quelle : l'ombre d'un texte sortait dix fois trop petite, souvent
 * jusqu'à l'invisible, alors que le drop-shadow des autres types de calque est
 * posé à l'EXTÉRIEUR du wrapper et sortait, lui, à la bonne taille.
 *
 * La chaîne d'entrée a toujours la forme « Xpx Ypx Bpx rgba(...) » (cf.
 * LabelCanvas et PrintView, seuls producteurs) : seules les longueurs en px
 * sont touchées, la couleur ne contient aucune unité.
 */
const echelleOmbreTexte = (css, k) => {
    if (!css || css === 'none') return 'none';
    return css.replace(/(-?\d*\.?\d+)px/g, (_, n) => `${Math.round(parseFloat(n) * k * 100) / 100}px`);
};

/**
 * Luminance relative (0 = noir, 1 = blanc) d'une couleur CSS simple.
 *
 * Ne comprend que les formes que produit l'éditeur : `#rgb`, `#rrggbb`,
 * `rgb()` et `rgba()`. Toute autre forme (nom CSS, hsl…) renvoie null : au
 * lieu de deviner, l'appelant retombe alors sur le voile clair, sûr pour le
 * texte noir qui est le cas de loin le plus fréquent sur une étiquette.
 */
const luminanceRelative = (css) => {
    if (!css || typeof css !== 'string') return null;
    let r, v, b;
    const hex = css.trim().match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
    if (hex) {
        const h = hex[1].length === 3 ? hex[1].split('').map(c => c + c).join('') : hex[1];
        r = parseInt(h.slice(0, 2), 16); v = parseInt(h.slice(2, 4), 16); b = parseInt(h.slice(4, 6), 16);
    } else {
        const rgb = css.trim().match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
        if (!rgb) return null;
        r = +rgb[1]; v = +rgb[2]; b = +rgb[3];
    }
    const lin = (c) => { const s = c / 255; return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4); };
    return 0.2126 * lin(r) + 0.7152 * lin(v) + 0.0722 * lin(b);
};

/**
 * Voile posé sous le texte pendant l'édition inline.
 *
 * Il ne sert qu'à cacher le décor (visuel IA, aplats, pictogrammes) pour qu'on
 * lise ce qu'on tape. C'était un gris fixe `rgba(150,150,150,0.85)` : un texte
 * gris — cas réel des mentions légales importées — se retrouvait écrit sur un
 * fond de sa propre valeur, donc illisible (retour utilisateur du 2026-08-20).
 *
 * Le voile se choisit désormais à l'OPPOSÉ de la couleur du texte, et
 * l'utilisateur peut le forcer ou le retirer (préférence `textEditVeil`).
 */
const VOILE_CLAIR = 'rgba(250,250,250,0.94)';
const VOILE_SOMBRE = 'rgba(18,18,20,0.90)';

const voileEdition = (mode, couleurTexte) => {
    if (mode === 'aucun') return { fond: 'transparent', clair: false };
    if (mode === 'sombre') return { fond: VOILE_SOMBRE, clair: false };
    if (mode === 'clair') return { fond: VOILE_CLAIR, clair: true };
    const l = luminanceRelative(couleurTexte);
    return (l !== null && l > 0.45)
        ? { fond: VOILE_SOMBRE, clair: false }
        : { fond: VOILE_CLAIR, clair: true };
};

/**
 * Render d'un calque texte avec rich text Tiptap.
 *
 * Deux modes :
 * - LECTURE : rendu HTML via richTextToHTML(el.content), avec stroke optionnel
 *   et text-shadow. Wrapper scale-up × TEXT_SCALE puis scale-down.
 * - ÉDITION : déclenché par double-clic. InlineRichTextEditor monté
 *   éphèmèrement, toolbar flottante via Portal. La sortie remet en mode
 *   LECTURE avec le contenu mis à jour via updateElement.
 *
 * @param {object} props
 * @param {object} props.el - Élément du calque texte
 * @param {object} props.canvas - canvas state (pour canvas.scale)
 * @param {string|null} props.editingId - id du calque actuellement en édition
 * @param {function} props.setEditingId - setter du state editingId
 * @param {function} props.selectElement - action store pour sélectionner un calque
 * @param {function} props.updateElement - action store pour MAJ un calque
 * @param {object} props.textContainerRefs - refs partagées des wrappers texte
 * @param {string} props.textShadowCSS - chaîne CSS text-shadow pré-calculée, dans
 *   l'unité du canvas : remise à l'échelle du wrapper ici (cf. echelleOmbreTexte)
 * @param {string} props.backgroundStyle - chaîne CSS background pré-calculée (gradient ou 'none')
 * @param {boolean} [props.renduExport] - rendu de VÉRITÉ (PlancheEtiquette :
 *   export, aperçu partagé, présentation) : aucun repère d'édition n'est monté
 */
export default function TextLayer({
    el,
    canvas,
    editingId,
    setEditingId,
    selectElement,
    updateElement,
    textContainerRefs,
    textShadowCSS,
    backgroundStyle,
    renduExport = false,
}) {
    const isEditingText = el.id === editingId;
    const modeVoile = useLabelStore((s) => s.textEditVeil) || 'auto';
    const voile = voileEdition(modeVoile, el.color || '#666666');
    const textStrokeEnabled = el.strokeEnabled;
    const lineHeightRatio = el.lineHeight !== undefined ? el.lineHeight : 1.2;

    // Garde TEXT_SCALE actif aussi en édition : sans ça, fontSize en
    // édition est rendu à sa taille physique (4-5px pour 12pt) →
    // espaces quasi invisibles + bugs de rendu sub-pixel.
    const effectiveScale = TEXT_SCALE;
    const effectiveFontSize = Math.round(el.size * PT_TO_MM * effectiveScale * 100) / 100;
    const textStrokeWidth = (el.strokeWidth !== undefined ? el.strokeWidth : 2) * effectiveScale;
    const textStrokeStyle = el.strokeType === 'gradient' ? getCssGradient(el.strokeGradient) : 'none';
    const textStrokeColor = el.strokeType === 'gradient' ? 'transparent' : (el.strokeColor || '#FFFFFF');
    // L'ombre est déclarée dans le wrapper grossi : elle suit la même règle que
    // la fontSize et l'interlettrage (cf. echelleOmbreTexte).
    const textShadowMisAEchelle = echelleOmbreTexte(textShadowCSS, effectiveScale);

    const textContainerStyle = {
        fontFamily: `"${el.font}", sans-serif`,
        fontSize: `${effectiveFontSize}px`,
        lineHeight: lineHeightRatio,
        letterSpacing: `${(el.letterSpacing !== undefined ? el.letterSpacing : 0) * effectiveScale}px`,
        fontWeight: el.fontWeight || 'normal',
        fontStyle: el.fontStyle || 'normal',
        textTransform: el.textTransform || 'none',
        textAlign: el.textAlign,
        textRendering: 'geometricPrecision',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        overflow: 'visible',
    };

    if (!textContainerRefs.current[el.id]) {
        textContainerRefs.current[el.id] = React.createRef();
    }
    const containerRef = textContainerRefs.current[el.id];

    return (
        <div
            ref={containerRef}
            className="relative w-full h-full"
            data-text-container={el.id}
            style={{
                cursor: isEditingText ? 'text' : 'move',
                userSelect: isEditingText ? 'text' : 'none',
                WebkitUserSelect: isEditingText ? 'text' : 'none',
                overflow: 'hidden',
            }}
            onDoubleClick={(e) => {
                if (el.locked) return;
                // Calques réglementaires : contenu normalement protégé (contentLocked).
                // Édition TEMPORAIREMENT autorisée via le flag (cf. utils/regulatoryFlags).
                if (el.contentLocked && !REGULATORY_CONTENT_EDIT_ALLOWED) {
                    e.stopPropagation();
                    return;
                }
                e.stopPropagation();
                selectElement(el.id);
                setEditingId(el.id);
            }}
        >
            {/* Repère d'ÉDITION, jamais monté dans le rendu de vérité. Il n'y
                était caché que par une règle CSS que chaque page de
                PlancheEtiquette devait recopier : la planche nue le peignait. */}
            {!isEditingText && !renduExport && <TextOverflowIndicator elId={el.id} />}
            {isEditingText ? (
                <>
                    <div style={{
                        position: 'absolute', inset: 0,
                        background: voile.fond,
                        pointerEvents: 'none',
                    }} />
                    {/* Cadre d'édition — tracé en dégradés répétés plutôt qu'avec
                        `border: dashed`, dont le browser tire des tirets de trois
                        fois l'épaisseur du trait : au zoom de travail ils lisaient
                        comme un gros pointillé. Ici le motif est fixé à la main,
                        3 px de tiret pour 3 px de vide, trait de 1 px à l'écran. */}
                    {(() => {
                        const trait = 1 / canvas.scale;
                        // Sur voile clair, l'ambre #eab308 passe presque inaperçu :
                        // le cadre prend alors sa version foncée. Même repère, même
                        // famille de couleur, contraste tenu dans les deux cas.
                        const teinte = voile.clair ? '#a16207' : '#eab308';
                        const motif = `${3 / canvas.scale}px`;
                        const periode = `${6 / canvas.scale}px`;  // 3 px de tiret + 3 px de vide
                        const tirets = (sens) => `repeating-linear-gradient(${sens}, ${teinte} 0 ${motif}, transparent ${motif} ${periode})`;
                        return (
                            <div style={{
                                position: 'absolute', inset: 0,
                                pointerEvents: 'none',
                                backgroundImage: `${tirets('to right')}, ${tirets('to right')}, ${tirets('to bottom')}, ${tirets('to bottom')}`,
                                backgroundSize: `100% ${trait}px, 100% ${trait}px, ${trait}px 100%, ${trait}px 100%`,
                                backgroundPosition: 'left top, left bottom, left top, right top',
                                backgroundRepeat: 'no-repeat',
                            }} />
                        );
                    })()}
                    {/* Wrapper scale-up (TEXT_SCALE×) puis scale-down — comme le mode lecture.
                        Permet à Tiptap d'avoir une fontSize CSS réelle de plusieurs dizaines
                        de px (pour ne pas casser le rendu des espaces / sub-pixel) tout en
                        apparaissant à la bonne taille visuelle dans le canvas. */}
                    <div style={{
                        position: 'absolute',
                        top: 0, left: 0,
                        width: `${100 * TEXT_SCALE}%`,
                        height: `${100 * TEXT_SCALE}%`,
                        transform: `scale(${1 / TEXT_SCALE})`,
                        transformOrigin: 'top left',
                        color: el.color || '#666666',
                        ...textContainerStyle,
                        background: 'transparent',
                    }}>
                        {/* Fallback null : le calque garde son cadre jaune d'édition
                            pendant les ~100ms du premier chargement du chunk TipTap. */}
                        <Suspense fallback={null}>
                        <InlineRichTextEditor
                            el={el}
                            containerRef={containerRef}
                            defaultStyle={{
                                fontFamily: `"${el.font}", sans-serif`,
                                fontSize: effectiveFontSize,
                                color: el.color || '#666666',
                                lineHeight: lineHeightRatio,
                                textAlign: el.textAlign,
                            }}
                            onChange={(json) => {
                                const plain = (json.content || []).map(p =>
                                    (p.content || []).map(t => t.text || '').join('')
                                ).join('\n');
                                // Sync : si l'alignement a été modifié via la toolbar Tiptap
                                // (sur un paragraphe), on le propage à el.textAlign pour
                                // garder l'inspecteur en cohérence.
                                const firstParaAlign = json.content?.[0]?.attrs?.textAlign;
                                const updates = { content: json, text: plain };
                                if (firstParaAlign && firstParaAlign !== el.textAlign) {
                                    updates.textAlign = firstParaAlign;
                                }
                                updateElement(el.id, updates);
                            }}
                            onClose={() => setEditingId(null)}
                        />
                        </Suspense>
                    </div>
                </>
            ) : (
                <div data-text-inner="true" style={{
                    position: 'absolute',
                    top: 0, left: 0,
                    width: `${100 * TEXT_SCALE}%`,
                    height: `${100 * TEXT_SCALE}%`,
                    transform: `scale(${1 / TEXT_SCALE})`,
                    transformOrigin: 'top left',
                }}>
                    {/* Rich text HTML (avec runs stylés) ou texte brut selon disponibilité de el.content */}
                    {(() => {
                        const hasRichText = el.content && el.content.content;
                        // Pas de DOMPurify ici : le HTML est généré par notre propre
                        // fonction richTextToHTML qui escape déjà les caractères
                        // dangereux et utilise un whitelist contrôlé de tags. DOMPurify
                        // peut potentiellement collapser certains espaces lors du
                        // parse/sérialise round-trip → on l'évite pour préserver les
                        // espaces inter-mots tels que tapés.
                        const richHTML = hasRichText
                            ? richTextToHTML(el.content)
                            : null;

                        const renderTextBody = (extraStyle) => {
                            if (hasRichText) {
                                return (
                                    <div
                                        style={{ ...textContainerStyle, ...extraStyle }}
                                        dangerouslySetInnerHTML={{ __html: richHTML }}
                                    />
                                );
                            }
                            return (
                                <div style={{ ...textContainerStyle, ...extraStyle }}>
                                    {el.text || ''}
                                </div>
                            );
                        };

                        return (
                            <>
                                {textStrokeEnabled && renderTextBody({
                                    position: 'absolute', inset: 0,
                                    WebkitTextStroke: `${textStrokeWidth * 2}px ${textStrokeColor}`,
                                    color: textStrokeColor,
                                    backgroundImage: el.strokeType === 'gradient' ? textStrokeStyle : 'none',
                                    WebkitBackgroundClip: el.strokeType === 'gradient' ? 'text' : 'border-box',
                                    backgroundClip: el.strokeType === 'gradient' ? 'text' : 'border-box',
                                    WebkitTextFillColor: el.strokeType === 'gradient' ? 'transparent' : undefined,
                                    textShadow: textShadowMisAEchelle,
                                    zIndex: 1, pointerEvents: 'none',
                                })}
                                {renderTextBody({
                                    position: 'absolute', inset: 0,
                                    color: el.fillType === 'gradient' ? 'transparent' : el.color,
                                    backgroundImage: el.fillType === 'gradient' ? backgroundStyle : 'none',
                                    WebkitBackgroundClip: el.fillType === 'gradient' ? 'text' : 'border-box',
                                    backgroundClip: el.fillType === 'gradient' ? 'text' : 'border-box',
                                    WebkitTextFillColor: el.fillType === 'gradient' ? 'transparent' : undefined,
                                    textShadow: textShadowMisAEchelle,
                                    zIndex: 2,
                                })}
                            </>
                        );
                    })()}
                </div>
            )}
        </div>
    );
}
